<template>
  <form
    class="form-template"
    data-cy="form"
    ref="form"
    :name="title"
    @submit.prevent
  >
    <v-ons-progress-bar v-if="isMultiStage" :value="progress" secondary="100" />

    <transition name="fade-out-left" mode="out-in" appear>
      <template v-if="showResults">
        <div class="p-3 container">
          <h3 class="mt-0">Results</h3>

          <template v-if="isFetchingSubmission">
            <div :style="{ width: '100%' }">
              <placeholder-rect full-width height="200px" class="m-0" />
            </div>
          </template>
          <form-submission-table
            v-else-if="currentSubmission"
            rounded
            :values="currentSubmission.values"
            :submission="currentSubmission"
            :groups="groups"
            :can-view-correct-answers="canViewCorrectAnswers"
            class="mb-2"
            @input="updateCurrentSubmission"
          />
        </div>
      </template>
      <template v-else-if="isSubmitted">
        <div class="p-3 pt-5 container text-center">
          <v-icon
            class="mt-3"
            icon="done_all"
            size="3rem"
            color="var(--primary-brand-color-hex)"
          />
          <h2 class="mb-0">{{ submittedHeading }}</h2>
          <p class="text-grey-70 mt-2">{{ submittedDescription }}</p>
        </div>
      </template>
      <template v-else-if="isIntro">
        <div class="p-3 pt-5 mt-3 container text-center">
          <v-icon
            icon="done_all"
            size="3rem"
            color="var(--primary-brand-color-hex)"
          />
          <h2 class="mb-0">{{ introHeading }}</h2>
          <p class="text-grey-70 mt-2">{{ introDescription }}</p>
        </div>
      </template>
      <template v-else-if="isReady && isPresent(currentGroup)">
        <div class="p-2 container">
          <form-group
            :group="currentGroup"
            :values="submitValues"
            :is-missable="isMissable"
            @input="updateFormSubmissionValue"
          />
        </div>
      </template>
    </transition>

    <v-made-with-movement v-if="!isDesktop" />

    <cta-row
      v-if="hideClose && isSubmitted && !showResults ? hasContinueAction : true"
      :overlay-modal="!inline"
      white-bg
    >
      <div class="d-flex justify-content-end w-100 align-items-center">
        <template v-if="showResults">
          <v-button
            secondary
            data-cy="form-resubmit"
            class="mr-1 form-template__back-button"
            @click="handleResubmit"
          >
            <v-icon icon="arrow_back" />
          </v-button>
          <v-button block @click="handleDone"> Done </v-button>
        </template>
        <template v-else-if="isSubmitted">
          <v-button block @click="close" data-cy="form-close">
            <template v-if="hasContinueAction">
              {{ $t("components.pageTemplates.formTemplate.continue") }}
            </template>
            <template v-else>
              {{ $t("components.pageTemplates.formTemplate.close") }}
            </template>
          </v-button>
        </template>
        <template v-else-if="isIntro">
          <v-button block @click="() => (isIntro = false)" data-cy="form-start">
            {{ $t("components.pageTemplates.formTemplate.begin") }}
          </v-button>
        </template>
        <template v-else>
          <v-button
            v-if="!isFirstGroup"
            ghost
            @click="goBack"
            class="pl-2 mr-auto form-template__back-button"
            data-cy="form-back"
          >
            {{ $t("components.pageTemplates.formTemplate.back") }}
          </v-button>

          <template v-if="isFinalGroup">
            <v-button
              block
              type="submit"
              :disabled="!isValid"
              :is-loading="isSubmitting"
              @click="submit"
              data-cy="form-submit"
            >
              {{ submitText }}
            </v-button>
          </template>

          <template v-else>
            <v-button
              class="ml-auto px-5"
              primary
              :is-loading="isAdvancing"
              :disabled="!isValid"
              data-cy="form-next"
              @click="handleNext"
            >
              {{ $t("components.pageTemplates.formTemplate.next") }}
            </v-button>
          </template>
        </template>
      </div>
    </cta-row>
  </form>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex"

import { isPresent } from "@/lib/utils"
import {
  FIELD_TYPES,
  CAPTURE_STATUS,
  forEachGroupField,
  getAssessmentFields,
  MISSED_VALUE
} from "@/lib/form-utils"
import EventBus, { EVENTS } from "@/lib/event-bus"

import FormGroup from "@/components/FormGroup"
import CtaRow from "@/components/CtaRow"
import PlaceholderRect from "@/components/PlaceholderRect"

export default {
  props: {
    values: { type: Object, required: true },
    groups: { type: Array, required: true },
    id: { type: [Number, String], default: null },
    title: { type: String, default: null },
    trigger: { type: String, default: "" },
    triggeringItem: { type: Object, default: () => ({}) },
    hasContinueAction: { type: Boolean, default: false },
    submittedHeading: {
      type: String,
      default: function() {
        return this.$t("components.pageTemplates.formTemplate.thanksForSubmit")
      }
    },
    submittedDescription: {
      type: String,
      default: function() {
        return this.$t(
          "components.pageTemplates.formTemplate.closeWindowMessage"
        )
      }
    },
    showIntroScreen: {
      type: Boolean,
      default: false
    },
    introHeading: {
      type: String,
      default: ""
    },
    introDescription: {
      type: String,
      default: ""
    },
    submitText: {
      type: String,
      default: function() {
        return this.$t("components.pageTemplates.formTemplate.submit")
      }
    },
    activeGroupIndex: { type: Number, default: 0 },
    showGroupUid: { type: String, default: null },
    showFieldUid: { type: String, default: null },
    isMissable: { type: Boolean, default: false },
    hideClose: { type: Boolean, default: false },
    inline: { type: Boolean, default: false },
    forceShowSubmittedScreen: { type: Boolean, default: false }
  },

  model: {
    prop: "values",
    event: "input"
  },

  components: {
    FormGroup,
    CtaRow,
    PlaceholderRect,
    FormSubmissionTable: () => import("@/components/FormSubmissionTable")
  },

  data() {
    return {
      isIntro: this.showIntroScreen,
      isSubmitting: false,
      isSubmitted: false,
      isAdvancing: false,
      isReady: false,
      isValid: true,
      isFetchingSubmission: false,
      showResults: false,
      currentSubmission: null,
      currentGroup: {},
      submitValues: { ...this.values },
      FIELD_TYPES
    }
  },

  beforeDestroy() {
    if (this.isSubmitted) {
      this.handleDone()
    }
  },

  mounted() {
    if (this.forceShowSubmittedScreen) {
      this.isSubmitted = true
    } else {
      this.prepareFields()
    }

    this.isReady = true
  },

  computed: {
    ...mapState("responsive", ["isDesktop"]),
    ...mapState("pages", ["currentPage"]),

    isPartialCapture() {
      return !!this.showGroupUid || !!this.showFieldUid
    },

    isFinalGroup() {
      return this.currentGroup.id === this.groups[this.groups.length - 1].id
    },

    isFillingRounds() {
      return this.groups.every(g => g.uid === this.currentGroup.uid)
    },

    isFirstGroup() {
      return this.currentGroup.id === this.groups[0].id
    },

    isMultiStage() {
      return this.groups.length > 1
    },

    canViewCorrectAnswers() {
      return (
        isPresent(this.currentSubmission) &&
        isPresent(this.currentSubmission.form) &&
        !!this.currentSubmission.form.can_view_correct_answers
      )
    },

    progress() {
      return this.isSubmitted
        ? 100
        : ((this.groups.findIndex(g => g.id === this.currentGroup.id) + 1) /
            this.groups.length) *
            100
    },

    requiredGroups() {
      return this.groups.filter(g => this.isGroupRequired(g))
    },

    hasSubscriberProperties() {
      let result = false

      this.groups.forEach(group => {
        forEachGroupField(group, field => {
          if (field.subscriber_property_id) {
            result = true
          }
        })
      })

      return result
    },

    hasAssessmentFields() {
      return getAssessmentFields(this.groups).length > 0
    }
  },

  methods: {
    isPresent,

    ...mapActions("forms", [
      "submitForm",
      "fetchFormSubmission",
      "updateSubmissionValues"
    ]),
    ...mapActions("analytics", ["trackFormProgressed"]),
    ...mapActions("subscriberProperties", ["fetchSubmittedProperties"]),
    ...mapMutations("forms", ["resetFormSubmissionValues", "setCaptureStatus"]),

    prepareFields() {
      this.groups.forEach((group, index) => {
        group._index = index
      })

      if (isPresent(this.currentGroup)) {
        this.setCurrentGroup(this.groups[this.currentGroup._index])
      } else {
        this.setCurrentGroup(this.groups[this.activeGroupIndex])
      }

      this.isValid = this.isGroupValid(this.currentGroup)
    },

    updateCurrentSubmission(newValues) {
      this.updateSubmissionValues({
        formId: this.id,
        formSubmissionId: this.currentSubmission.id,
        values: newValues
      }).then(() => {
        for (let fieldId in newValues) {
          this.$set(this.currentSubmission.values, fieldId, newValues[fieldId])
        }

        this.updateFormSubmissionValue(newValues)
        this.$emit("input", this.submitValues)
      })
    },

    handleShowResults(submissionId) {
      this.showResults = true
      this.isFetchingSubmission = true

      this.fetchFormSubmission({
        formId: this.id,
        formSubmissionId: submissionId
      })
        .then(submission => {
          this.currentSubmission = submission
          this.updateFormSubmissionValue(submission.values)
        })
        .catch(() => {
          this.handleDone()
        })
        .finally(() => {
          this.isFetchingSubmission = false
        })
    },

    handleResubmit() {
      this.showResults = false
      this.isSubmitted = false
    },

    handleDone() {
      this.showResults = false
      this.currentSubmission = null
      this.resetFormSubmissionValues(this.id)
    },

    gotoNextGroup() {
      setTimeout(() => {
        this.setCurrentGroup(
          this.groups[
            this.groups.findIndex(g => g.id === this.currentGroup.id) + 1
          ]
        )

        this.trackProgressed("forwards")
      }, 150)
    },

    handleNext() {
      const currentGroup = this.currentGroup
      this.$emit("input", this.submitValues)

      if (this.isFinalGroup) {
        this.showIsAdvancing(() => {
          this.emitProgressCaptured(currentGroup)
          this.close()
        })
      } else {
        this.showIsAdvancing(() => {
          this.emitProgressCaptured(currentGroup)
          this.gotoNextGroup()
        })
      }
    },

    showIsAdvancing(onFinish = () => {}) {
      this.isAdvancing = true
      setTimeout(() => {
        onFinish()
        this.isAdvancing = false
      }, 150)
    },

    isGroupRequired(group) {
      let isRequired = false
      forEachGroupField(
        group,
        field => !field.is_required || (isRequired = true)
      )
      return isRequired
    },

    emitProgressCaptured(currentGroup) {
      if (this.requiredGroups.includes(currentGroup)) {
        // get form groups of same workout_group
        const groups = this.requiredGroups.filter(
          g => g.uid === currentGroup.uid
        )
        const groupsNumber = groups.length
        const groupIndex = groups.indexOf(currentGroup)

        forEachGroupField(currentGroup, f => {
          const value = this.submitValues[f.id]?.value
          const status =
            value === MISSED_VALUE
              ? CAPTURE_STATUS.MISSED
              : CAPTURE_STATUS.SAVED

          this.setCaptureStatus({
            uid: this.showFieldUid ? this.showFieldUid : f.uid,
            status,
            groupsNumber,
            groupIndex
          })
        })

        EventBus.$emit(EVENTS.FORM_PROGRESS_CAPTURED, this.isPartialCapture)
      }
    },

    submit(e) {
      if (this.isValid) {
        this.$emit("input", this.submitValues)

        if (this.isPartialCapture) {
          this.emitProgressCaptured(this.currentGroup)
          this.close()
        } else if (this.id) {
          this.isSubmitting = true

          this.submitForm({
            formId: this.id,
            triggering_item: this.triggeringItem,
            trigger: this.trigger,
            pageId: this.currentPage?.id
          })
            .then(({ data }) => {
              this.$emit("submit")
              this.isSubmitted = true

              if (this.hasAssessmentFields) {
                this.handleShowResults(data.submission_id)
              } else {
                this.handleDone()
              }

              EventBus.$emit(EVENTS.FORM_SUBMITTED, this.id)

              if (this.hasSubscriberProperties) {
                this.fetchSubmittedProperties()
              }
            })
            .finally(() => (this.isSubmitting = false))

          this.$refs.form.dispatchEvent(
            new window.SubmitEvent("submit", { submitter: e.target })
          )
        }
      }
    },

    goBack() {
      this.setCurrentGroup(
        this.groups[
          this.groups.findIndex(g => g.id === this.currentGroup.id) - 1
        ]
      )

      this.trackProgressed("back")
    },

    setCurrentGroup(group) {
      this.currentGroup = group
      this.isValid = this.isGroupValid(this.currentGroup)
    },

    close() {
      this.$emit("close")
    },

    isFieldValid(field) {
      const { value, image } = this.submitValues[field.id] || {}

      switch (field.field_type) {
        case FIELD_TYPES.ACCEPTANCE:
          return !!value
        case FIELD_TYPES.IMAGE_UPLOAD:
          return field.is_required ? !!image : true
        default:
          return field.is_required ? !!value : true
      }
    },

    isGroupValid(group) {
      let isValid = true
      forEachGroupField(
        group,
        field => this.isFieldValid(field) || (isValid = false)
      )
      return isValid
    },

    trackProgressed(direction = "forwards") {
      if (!this.currentGroup) return

      this.trackFormProgressed({
        form: {
          id: this.id,
          title: this.title,
          trigger: this.trigger
        },
        group: this.currentGroup,
        direction
      })
    },

    updateFormSubmissionValue(values) {
      this.submitValues = values

      this.isValid = this.isGroupValid(this.currentGroup)
    }
  },

  watch: {
    values(values) {
      this.submitValues = { ...values }
    },

    groups() {
      this.prepareFields()
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.form-template {
  .made-with-movement {
    margin-top: 1rem;
    margin-bottom: 1rem;
  }

  .progress-bar--material {
    height: $size--2;
  }

  .progress-bar--material__primary {
    background-color: var(--primary-brand-color-hex);
  }
}

.form-template__back-button {
  flex-shrink: 0;
}

.form-template__button-navigate {
  min-width: 90px; // Avoid truncating text

  &:disabled {
    opacity: 0.2 !important;
  }
}
</style>
